// PaymentHistory.jsx
import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import LoadingSpinner from "../components/LoadingSpinner";
import axios from "axios";
import { createRoot } from "react-dom/client";
import "../assets/css/results-view.css"; // reuse

export default function PaymentHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const API =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_BASE_URL
      : "http://localhost:3003";
  const matricNo = localStorage.getItem("matricNo");

  useEffect(() => {
    async function fetchHistory() {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/payments/history`, {
          params: { matricNo },
        });
        setHistory(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);
        setError("Failed to load payment history.");
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, [API, matricNo]);

  const fmtAmount = (amt) => {
    if (amt == null) return "₦0.00";
    const n = Number(amt);
    if (Number.isNaN(n)) return "₦0.00";
    return (
      "₦" +
      n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const fmtDate = (isoOrNull) => {
    if (!isoOrNull) return "N/A";
    const d = new Date(isoOrNull);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleString(); // show date + time in user's locale
  };

  // Print handler: opens new window, mounts PrintPage, triggers print
  async function handlePrint(record) {
    try {
      // fetch student biodata for display in print (fallback to record if not available)
      let biodata = null;
      try {
        const bioResp = await fetch(
          `${API}/api/biodata/${encodeURIComponent(matricNo)}`,
        );
        if (bioResp.ok) biodata = await bioResp.json();
      } catch (err) {
        // ignore — we can proceed with record fields
      }

      const css = `
        /* Print CSS (embedded) */
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; margin: 20px; color: #222; }
        .print-root { max-width: 800px; margin: 0 auto; }
        .header { display:flex; align-items:center; gap:16px; margin-bottom: 8px; }
        .logo { width:80px; height:auto; object-fit:contain; }
        .org { font-size:18px; font-weight:700; }
        .subtitle { font-size:13px; color:#333; margin-top:4px; }
        .line { border-bottom: 1px solid #ddd; margin: 12px 0; }
        .details { display:flex; justify-content:space-between; gap:12px; margin-bottom: 12px; flex-wrap:wrap; }
        .col { min-width:180px; }
        table { width:100%; border-collapse: collapse; margin-top:12px; }
        th, td { padding:8px 6px; border: 1px solid #e6e6e6; text-align:left; font-size:13px; }
        .right { text-align:right; }
        .big { font-size:16px; font-weight:700; }
        .muted { color:#666; font-size:12px; }
        .note { margin-top:14px; font-size:13px; color:#333; }
        .footer { margin-top:24px; display:flex; justify-content:space-between; align-items:center; }
        .signature { width:200px; border-top:1px solid #999; text-align:center; padding-top:6px; color:#444; }
        @media print {
          body { margin: 12px; }
          .no-print { display:none !important; }
        }
      `;

      const win = window.open("", "_blank", "width=900,height=800");
      if (!win) {
        alert("Popup blocked. Allow popups for this site to print.");
        return;
      }

      win.document.write(
        `<!doctype html><html><head><title>Print Preview</title><style>${css}</style></head><body><div id="print-root"></div></body></html>`,
      );
      win.document.close();
      win.focus();

      // small delay to allow window to be ready
      setTimeout(() => {
        const rootEl = win.document.getElementById("print-root");
        const root = createRoot(rootEl);
        root.render(<PrintPage payment={record} biodata={biodata} />);
        // wait for React to render, then call print
        setTimeout(() => {
          try {
            win.print();
          } catch (e) {
            console.error("Print error:", e);
          }
          // close on a short delay so user can see print dialog first
          setTimeout(() => {
            try {
              win.close();
            } catch (e) {}
          }, 500);
        }, 300);
      }, 150);
    } catch (err) {
      console.error("handlePrint error:", err);
      alert("Unable to print. See console for details.");
    }
  }

  // PrintPage component: renders either a Receipt (Paid) or Invoice (Pending)
  function PrintPage({ payment, biodata }) {
    const isPaid = String(payment?.Status ?? "").toLowerCase() === "paid";
    const studentName =
      (biodata && biodata.fullName) || payment.StudentName || "Student";
    const programme =
      (biodata && (biodata.department || biodata.programme)) ||
      payment.Programme ||
      "";
    const level = payment.Level ?? "";
    const semester = payment.Semester ?? "";
    const amount = payment.Amount ?? payment.amount ?? 0;
    const rrr = payment.TransactionID ?? payment.RRR ?? "N/A";
    const datePaid = payment.PaymentDate ?? payment.PaidAt ?? null;
    const createdAt = payment.CreatedAt ?? payment.createdAt ?? null;

    return (
      <div className="print-root">
        <div className="header">
          {/* Replace with your actual logo url if available */}
          <img className="logo" src="/logo192.png" alt="Institution logo" />
          <div>
            <div className="org">Lagos University Teaching Hospital</div>
            <div className="subtitle">
              School Fees {isPaid ? "Receipt" : "Invoice"}
            </div>
          </div>
        </div>

        <div className="line" />

        <div className="details">
          <div className="col">
            <div>
              <span className="muted">Student:</span>{" "}
              <strong>{studentName}</strong>
            </div>
            <div className="muted">
              Matric No: <strong>{payment.MatricNumber ?? matricNo}</strong>
            </div>
            <div className="muted">
              Programme: <strong>{programme}</strong>
            </div>
          </div>

          <div className="col">
            <div>
              <span className="muted">Level:</span> <strong>{level}</strong>
            </div>
            <div>
              <span className="muted">Semester:</span>{" "}
              <strong>{semester}</strong>
            </div>
            <div>
              <span className="muted">Transaction (RRR):</span>{" "}
              <strong>{rrr}</strong>
            </div>
          </div>

          <div className="col right">
            <div>
              <span className="muted">Status:</span>{" "}
              <strong>{payment.Status ?? "Unknown"}</strong>
            </div>
            <div className="muted">
              Reference ID: <strong>{payment.PaymentID}</strong>
            </div>
            <div className="muted">
              Date:{" "}
              <strong>{isPaid ? fmtDate(datePaid) : fmtDate(createdAt)}</strong>
            </div>
          </div>
        </div>

        <div className="line" />

        {isPaid ? (
          <>
            <div style={{ marginTop: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      School Fees Payment ({semester} - Level {level})
                    </td>
                    <td className="right">{fmtAmount(amount)}</td>
                  </tr>
                  <tr>
                    <td className="big">Total Paid</td>
                    <td className="right big">{fmtAmount(amount)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="note">
                Thank you for your payment. This receipt confirms that payment
                has been received.
              </p>
            </div>
            <div className="footer">
              <div className="muted">
                Generated: {new Date().toLocaleString()}
              </div>
              <div className="signature">Finance Officer</div>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginTop: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      School Fees Invoice ({semester} - Level {level})
                    </td>
                    <td className="right">{fmtAmount(amount)}</td>
                  </tr>
                  <tr>
                    <td className="big">Amount Due</td>
                    <td className="right big">{fmtAmount(amount)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="note">
                This is an invoice for the outstanding school fees. Payment is
                not yet received. Use the RRR above to complete payment. If you
                have already paid, click "Verify Payment" on the portal.
              </p>
            </div>
            <div className="footer">
              <div className="muted">
                Generated: {new Date().toLocaleString()}
              </div>
              <div className="signature">Accounts / Bursary</div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="result-page" style={{ marginTop: "-130px" }}>
      <Sidebar />
      <div className="result-main">
        <main className="results-view-page">
          <h1>Payment History</h1>

          {loading && <LoadingSpinner />}
          {error && <p className="error">{error}</p>}

          {!loading && history.length === 0 && (
            <p className="message">No payments found.</p>
          )}

          {!loading && history.length > 0 && (
            <table className="results-table">
              <thead>
                <tr>
                  <th>Fee Type</th>
                  <th>Level</th>
                  <th>Semester</th>
                  <th>Amount</th>
                  <th>Transaction ID (RRR)</th>
                  <th>Status</th>
                  <th>Date Paid</th>
                  <th className="no-print">Print</th>
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.PaymentID}>
                    <td>{p.FeeType ?? "School Fees Payment"}</td>
                    <td>{p.Level ?? ""}</td>
                    <td>{p.Semester ?? ""}</td>
                    <td>{fmtAmount(p.Amount)}</td>
                    <td>{p.TransactionID ?? p.RRR ?? "N/A"}</td>
                    <td>{p.Status ?? "Unknown"}</td>
                    <td>{fmtDate(p.PaymentDate ?? p.PaidAt ?? p.CreatedAt)}</td>
                    <td className="no-print">
                      <button className="btn" onClick={() => handlePrint(p)}>
                        Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </main>
      </div>
    </div>
  );
}
